import { Suspense, lazy, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Spin } from 'antd';
import { MainLayout } from '../layouts/MainLayout';
import { useRootStore } from './StoreProvider';
import { ErrorBoundary } from '../../shared/components/ErrorBoundary';
import { needPickHotel } from '../../features/hotel-picker/pick-flag';

// --- Lazy-loaded pages ---
const RoomsPilotRoute = lazy(() => import('../../features/rooms-pilot/RoomsPilotRoute'));
// Operations Control Tower, экран «Сегодня». Под флагом
// /ops-today.json — как Rooms Pilot.
const OpsTodayRoute = lazy(() => import('../../features/ops-today/OpsTodayRoute'));
const ReceptionOrderRoute = lazy(() => import('../../features/reception-order/ReceptionOrderRoute'));
const LoginPage = lazy(() => import('../../views/login/LoginPage'));
// Регистрация «сначала аккаунт» и «Добро пожаловать» (HKeeper 2.0).
const SignupPage = lazy(() => import('../../features/signup/SignupPage'));
const StartPage = lazy(() => import('../../features/signup/StartPage'));
const HotelPickerPage = lazy(() => import('../../features/hotel-picker/HotelPickerPage'));
const ForgotPasswordPage = lazy(() => import('../../views/login/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('../../views/login/ResetPasswordPage'));
const DashboardPage = lazy(
  () => import('../../features/ops-today/MainDashboardRoute'),
);
const HousekeepingDashboardPage = lazy(
  () => import('../../features/housekeeping-r3/HousekeepingRoute'),
);
const HousekeeperProfilePage = lazy(
  () => import('../../views/housekeeping/HousekeeperProfilePage'),
);
const TasksPage = lazy(
  () => import('../../views/housekeeping/TasksPage'),
);
const RoomsPage = lazy(
  () => import('../../views/housekeeping/RoomsPage'),
);
const PlannerPage = lazy(
  () => import('../../views/housekeeping/PlannerPage'),
);
const InventoryPage = lazy(
  () => import('../../views/housekeeping/InventoryPage'),
);
const HousekeepingReportsPage = lazy(
  () => import('../../views/housekeeping/ReportsPage'),
);
const WifiRegistrationPage = lazy(
  () => import('../../views/wifi/RegistrationPage'),
);
const WifiActivePage = lazy(
  () => import('../../views/wifi/ActivePage'),
);
const WifiTrustedDevicesPage = lazy(
  () => import('../../views/wifi/TrustedDevicesPage'),
);
const WifiHistoryPage = lazy(
  () => import('../../views/wifi/HistoryPage'),
);
const WifiConstructorPage = lazy(
  () => import('../../views/wifi/ConstructorPage'),
);
const WifiLandingV2ListPage = lazy(
  () => import('../../views/wifi/landing-v2/LandingV2ListPage'),
);
const WifiLandingV2EditorPage = lazy(
  () => import('../../views/wifi/landing-v2/LandingV2EditorPage'),
);
const WifiStatisticsPage = lazy(
  () => import('../../views/wifi/StatisticsPage'),
);
const GroupReputationPage = lazy(
  () => import('../../features/reviews-r3/NetworkRoute'),
);
const ReviewsPage = lazy(
  () => import('../../features/reviews-r3/ReviewsRoute'),
);
const SurveysPage = lazy(
  () => import('../../features/surveys-r3/SurveysRoute'),
);
const ConciergeCatalogPage = lazy(
  () => import('../../views/concierge/CatalogPage'),
);
const HotelMapPage = lazy(
  () => import('../../views/twin/HotelMapPage'),
);
const ConciergeOrdersPage = lazy(
  () => import('../../views/concierge/OrdersPage'),
);
const ConciergePaymentsPage = lazy(
  () => import('../../views/concierge/PaymentsPage'),
);
const ConciergeAnalyticsPage = lazy(
  () => import('../../views/concierge/AnalyticsPage'),
);
const ConciergeAppPage = lazy(
  () => import('../../views/concierge/AppPage'),
);
const ConciergeSchedulePage = lazy(
  () => import('../../views/concierge/SchedulePage'),
);
const ConciergeGuestsPage = lazy(
  () => import('../../views/concierge/GuestsPage'),
);
// /analytics: новая «Аналитика» в оболочке R3, иначе прежний дашборд.
const ConciergeDashboardPage = lazy(
  () => import('../../features/analytics/AnalyticsRoute'),
);
const LoyaltyRoute = lazy(() => import('../../features/analytics/LoyaltyRoute'));
// Консьерж → Обзор: очередь заказов и итоги периода (главная раздела).
const ConciergeOverviewPage = lazy(() => import('../../features/concierge-overview/OverviewPage'));
const ConciergeUpsellPage = lazy(
  () => import('../../views/concierge/UpsellPage'),
);
const ConciergeTransfersPage = lazy(
  () => import('../../views/concierge/TransfersPage'),
);
const ConciergeGiftCardsPage = lazy(
  () => import('../../views/concierge/GiftCardsPage'),
);
const ConciergeEventsPage = lazy(
  () => import('../../views/concierge/EventsPage'),
);
const ConciergeFitnessPage = lazy(
  () => import('../../views/concierge/FitnessAdminPage'),
);
// Conference / MICE (конференц-залы) — Этап 1.
// Залы/Форматы/Оборудование объединены в один раздел с горизонтальными
// вкладками (RoomsHubPage, ?tab=rooms|layouts|equipment).
const ConferenceRoomsHubPage = lazy(
  () => import('../../views/conference/RoomsHubPage'),
);
const ConferenceEventsPage = lazy(
  () => import('../../views/conference/EventsPage'),
);
const ConferenceCalendarPage = lazy(
  () => import('../../views/conference/CalendarPage'),
);
const ConferenceInquiriesPage = lazy(
  () => import('../../views/conference/InquiriesPage'),
);
// Карта и Транспорт переехали в /concierge/app как табы (см. AppPage).
// Здесь lazy-импорты больше не нужны.
// VisaVouchersPage теперь загружается как таб внутри OrdersPage
const ProfilePage = lazy(
  () => import('../../views/profile/ProfilePage'),
);
const CheckinHistoryPage = lazy(
  () => import('../../views/checkin/HistoryPage'),
);
const WelcomePage = lazy(
  () => import('../../views/checkin/WelcomePage'),
);
const BookingsPage = lazy(
  () => import('../../views/checkin/BookingsPage'),
);
const SchedulePage = lazy(
  () => import('../../views/checkin/SchedulePage'),
);
const DocumentsPage = lazy(
  () => import('../../views/checkin/DocumentsPage'),
);
const EKeysPage = lazy(
  () => import('../../views/checkin/EKeysPage'),
);
const CrmPage = lazy(
  () => import('../../views/crm/CrmPage'),
);
const OrganizationPage = lazy(
  () => import('../../views/settings/OrganizationPage'),
);
const StaffRoute = lazy(
  () => import('../../features/staff-r3/StaffRoute'),
);
const SettingsRoute = lazy(
  () => import('../../features/settings-r3/SettingsRoute'),
);
const AboutRoute = lazy(
  () => import('../../features/settings-r3/AboutRoute'),
);
const CleaningsRoute = lazy(
  () => import('../../features/settings-r3/CleaningsRoute'),
);
const ContactRoute = lazy(
  () => import('../../features/settings-r3/ContactRoute'),
);
const PaymentsRoute = lazy(
  () => import('../../features/settings-r3/PaymentsRoute'),
);
const ServiceTileRoute = lazy(
  () => import('../../features/settings-r3/ServiceTileRoute'),
);
const IntegrationsRoute = lazy(
  () => import('../../features/settings-r3/IntegrationsRoute'),
);
const TariffsR3Route = lazy(() =>
  import('../../features/settings-r3/ObjectPagesRoute').then((m) => ({ default: m.TariffsRoute })),
);
const DocsR3Route = lazy(() =>
  import('../../features/settings-r3/ObjectPagesRoute').then((m) => ({ default: m.DocsRoute })),
);
const PlatformsPage = lazy(
  () => import('../../views/settings/PlatformsPage'),
);
const PlatformUsersPage = lazy(
  () => import('../../views/settings/PlatformUsersPage'),
);
const AllUsersPage = lazy(
  () => import('../../views/settings/AllUsersPage'),
);
const PlatformServicesPage = lazy(
  () => import('../../views/settings/PlatformServicesPage'),
);
const MailTemplatesPage = lazy(
  () => import('../../views/settings/MailTemplatesPage'),
);
const RatesQuotasPage = lazy(
  () => import('../../views/rates/RatesQuotasPage'),
);
const ChannelManagerPage = lazy(
  () => import('../../views/settings/ChannelManagerPage'),
);
const PlaceholderPage = lazy(
  () => import('../../views/placeholder/PlaceholderPage'),
);
const OnboardingPage = lazy(
  () => import('../../views/onboarding/OnboardingPage'),
);
const OnboardingWelcomePage = lazy(
  () => import('../../views/welcome/WelcomePage'),
);
const PrivacyPolicyPage = lazy(
  () => import('../../views/onboarding/PrivacyPolicyPage'),
);
const SystemPlatformsPage = lazy(
  () => import('../../views/system/SystemPlatformsPage'),
);
const SystemBillingPage = lazy(
  () => import('../../views/system/SystemBillingPage'),
);
const SystemDealersPage = lazy(
  () => import('../../views/system/SystemDealersPage'),
);
const DealerPlatformsPage = lazy(
  () => import('../../views/dealer/DealerPlatformsPage'),
);
const DealerInvoicesPage = lazy(
  () => import('../../views/dealer/DealerInvoicesPage'),
);
const DealerInfoPage = lazy(
  () => import('../../views/dealer/DealerInfoPage'),
);
const SystemLeadsPage = lazy(
  () => import('../../views/system/SystemLeadsPage'),
);
const OnlineVisaAgentsPage = lazy(
  () => import('../../views/system/OnlineVisaAgentsPage'),
);
const OnlineVisaPartnerApplicationsPage = lazy(
  () => import('../../views/system/OnlineVisaPartnerApplicationsPage'),
);
const SystemNewsPage = lazy(
  () => import('../../views/system/SystemNewsPage'),
);
const SystemSupportPage = lazy(
  () => import('../../views/system/SystemSupportPage'),
);
const SystemPendingPage = lazy(
  () => import('../../views/system/SystemPendingPage'),
);
const WifiMonitoringPage = lazy(
  () => import('../../views/system/WifiMonitoringPage'),
);
const WifiSetupPage = lazy(
  () => import('../../views/system/WifiSetupPage'),
);
const ChangelogPage = lazy(() => import('../../views/changelog/ChangelogPage'));
const SupportPage = lazy(
  () => import('../../views/support/SupportPage'),
);
const HotelsTablePage = lazy(
  () => import('../../views/marketing/HotelsTablePage'),
);
const HotelKanbanPage = lazy(
  () => import('../../views/marketing/HotelKanbanPage'),
);
const AnalyticsPage = lazy(
  () => import('../../views/marketing/AnalyticsPage'),
);
const CampaignsPage = lazy(
  () => import('../../views/marketing/CampaignsPage'),
);
const SystemHealthPage = lazy(
  () => import('../../views/system/SystemHealthPage'),
);
const FlightWatchPage = lazy(
  () => import('../../views/system/FlightWatchPage'),
);
const SystemPartnersPage = lazy(
  () => import('../../views/system/SystemPartnersPage'),
);
const UnisenderSendPage = lazy(
  () => import('../../views/marketing/UnisenderSendPage'),
);
const UnisenderListsPage = lazy(
  () => import('../../views/marketing/UnisenderListsPage'),
);
const UnisenderTemplatesPage = lazy(
  () => import('../../views/marketing/UnisenderTemplatesPage'),
);
const UnisenderStatsPage = lazy(
  () => import('../../views/marketing/UnisenderStatsPage'),
);
const CampaignTrackingPage = lazy(
  () => import('../../views/marketing/CampaignTrackingPage'),
);
const LaundryPage = lazy(
  () => import('../../views/laundry/LaundryPage'),
);
const TasksHubPage = lazy(
  () => import('../../views/tasks/TasksHubPage'),
);
const RequestsPage = lazy(
  () => import('../../views/requests/RequestsPage'),
);
const SitePage = lazy(
  () => import('../../views/site/SitePage'),
);
const PairConfirmPage = lazy(
  () => import('../../views/tv/PairConfirmPage'),
);
const TvDevicesPage = lazy(
  () => import('../../views/tv/TvDevicesPage'),
);

// --- Loading fallback ---
function PageLoader() {
  return (
    <div className="flex h-full items-center justify-center" style={{ minHeight: '100vh' }}>
      <Spin size="large" />
    </div>
  );
}

// --- Полноэкранный лоадер при инициализации ---
function AppInitLoader() {
  return (
    <div
      className="flex items-center justify-center"
      style={{
        minHeight: '100vh',
        background: 'var(--color-bg-secondary)',
      }}
    >
      <div className="text-center">
        <Spin size="large" />
        <div className="mt-4" style={{ color: 'var(--color-text-tertiary)' }}>
          Загрузка...
        </div>
      </div>
    </div>
  );
}

/** Куда вести после входа: сохранённый в state путь (только внутренний). */
function afterLoginRoute(state: unknown, fallback: string): string {
  const from = (state as { from?: unknown } | null)?.from;
  if (typeof from === 'string' && from.startsWith('/') && !from.startsWith('//') && !from.startsWith('/login')) {
    return from;
  }
  return fallback;
}

// --- Приватный роут ---
/**
 * Старый конструктор Wi-Fi-лендингов — только отелям, у которых есть его
 * лендинги и не привязан Landing v2 (как в меню). Новым отелям — «Лендинги»
 * (решение 03.11.2026). Суперадмин и поддержка проходят всегда.
 */
const LegacyConstructorGate = observer(function LegacyConstructorGate({ children }: { children: React.ReactNode }) {
  const store = useRootStore();
  const p = store.platforms.activePlatform as any;
  if (!p) return <PageLoader />;
  const staff = store.profile?.is_superadmin === true || (store.profile as any)?.is_support === true;
  const allowed = staff || (p.wifi_legacy_landings === true && p.wifi_landing_v2_bound !== true);
  return allowed ? <>{children}</> : <Navigate to="/wifi/landing-v2" replace />;
});

/**
 * Стартовая страница. Сотруднику отеля ждём объект (до 3 с): от него
 * зависит, куда вести (Шахматка или дашборд), иначе первым кадром
 * всегда уходили бы на дашборд.
 */
const IndexRedirect = observer(function IndexRedirect({ to, waitPlatform }: { to: string; waitPlatform: boolean }) {
  const store = useRootStore();
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setTimedOut(true), 3000);
    return () => window.clearTimeout(t);
  }, []);
  if (waitPlatform && !store.platforms.activePlatform && !timedOut) return <PageLoader />;
  return <Navigate to={to} replace />;
});

const PrivateRoute = observer(function PrivateRoute({
  children,
}: {
  children: React.ReactNode;
}) {
  const store = useRootStore();
  const location = useLocation();

  if (!store.auth.isAuthenticated) {
    // Запоминаем, куда шли: deep link из экстранета Booking.com
    // (/settings/channel-manager, /settings/tariffs) после входа должен
    // открыть именно эту страницу, а не стартовую.
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }

  return <>{children}</>;
});

// --- Гейт онбординга ---
// Отключён 01.06.2026: после внедрения чек-листа онбординга у Гринвуда и
// других отелей через пару дней админ-портал начинал показывать белый
// экран — помогала только очистка cookies/localStorage. Mladen
// (и я тоже) каждые пару дней натыкались на это. Решили: онбординг
// был полезен раз при регистрации, но в повседневной работе только
// мешает. /welcome остаётся доступной по прямой ссылке для тех, кто
// хочет настроить руками, но никакого принудительного редиректа.
//
// Чтобы вернуть онбординг как обязательный — нужно (а) разобраться с
// причиной белого экрана через 2-3 дня, и (б) восстановить логику
// fetch + shouldRedirectToWelcome + AppInitLoader из git history (до
// этого коммита).
const OnboardingGate = observer(function OnboardingGate({ children }: { children: React.ReactNode }) {
  // Аккаунт после регистрации «сначала аккаунт»: отеля ещё нет — весь
  // кабинет пуст и закрыт сервером, поэтому только «Добро пожаловать».
  // Не редирект онбординга из прошлого (см. выше): только без отеля.
  const store = useRootStore();
  const profile = store.profile as {
    platform?: number | null;
    platforms?: unknown[];
    is_superadmin?: boolean;
    is_support?: boolean;
    access?: string;
  } | null;
  if (
    profile &&
    !profile.is_superadmin &&
    !profile.is_support &&
    // Без отеля — или отель уже создан, но заявка ещё не одобрена
    // (access=DENY): кабинет закрыт сервером, показываем статус.
    ((!profile.platform && !(profile.platforms && profile.platforms.length)) || profile.access === 'DENY')
  ) {
    return <Navigate to="/start" replace />;
  }
  // Несколько отелей — сначала выбор отеля (один раз за вход).
  if (needPickHotel()) {
    return <Navigate to="/choose-hotel" replace />;
  }
  return <>{children}</>;
});

// --- Гейт fitness_staff ---
// Сотрудник фитнес-стенда (group=fitness_staff) видит только пункт
// /concierge/fitness. Любой другой URL — мягкий редирект сюда. Гейт
// сидит внутри PrivateRoute → OnboardingGate, поэтому к моменту его
// работы profile уже подтянут.
const FitnessStaffGate = observer(function FitnessStaffGate({
  children,
}: {
  children: React.ReactNode;
}) {
  const store = useRootStore();
  const location = useLocation();

  const groupId =
    (store.profile as { group?: string; group_id?: string } | null)?.group ??
    (store.profile as { group_id?: string } | null)?.group_id ??
    null;
  const isFitnessStaff =
    !store.profile?.is_superadmin &&
    !store.profile?.is_support &&
    groupId === 'fitness_staff';
  // #378: аналогичный гейт для аниматора — все URL ≠ /concierge/schedule
  // мягко редиректим внутрь.
  const isAnimator =
    !store.profile?.is_superadmin &&
    !store.profile?.is_support &&
    groupId === 'animator';
  // Сотрудник прачечной — только /laundry (роль заявлена однораздельной,
  // но раньше гейта не было и видно было всё меню).
  const isLaundryStaff =
    !store.profile?.is_superadmin &&
    !store.profile?.is_support &&
    groupId === 'laundry_staff';

  if (
    isLaundryStaff &&
    !location.pathname.startsWith('/laundry')
  ) {
    return <Navigate to="/laundry" replace />;
  }
  if (
    isFitnessStaff &&
    !location.pathname.startsWith('/concierge/fitness')
  ) {
    return <Navigate to="/concierge/fitness" replace />;
  }
  if (
    isAnimator &&
    !location.pathname.startsWith('/concierge/schedule')
  ) {
    return <Navigate to="/concierge/schedule" replace />;
  }
  return <>{children}</>;
});

// --- Основной роутер ---
export const AppRouter = observer(function AppRouter() {
  const routerLocation = useLocation();
  const store = useRootStore();

  // Показываем лоадер пока проверяем существующий токен
  if (store.auth.isInitializing) {
    return <AppInitLoader />;
  }

  // Сотрудник фитнес-стенда (группа fitness_staff) видит только
  // модуль /concierge/fitness — отдельная роль для iPad-стенда у входа
  // в ФОК. Эта проверка должна стоять до общего landingRoute.
  const groupId =
    (store.profile as { group?: string; group_id?: string } | null)?.group ??
    (store.profile as { group_id?: string } | null)?.group_id ??
    null;
  const isFitnessStaff =
    !store.profile?.is_superadmin &&
    !store.profile?.is_support &&
    groupId === 'fitness_staff';

  // Лендинг после входа зависит от роли. Суперадмин видит список всех
  // платформ, техподдержка — сразу попадает к тикетам, fitness_staff —
  // на стенд фитнеса, обычный админ объекта — на дашборд своей платформы.
  const isLaundryStaff =
    !store.profile?.is_superadmin &&
    !store.profile?.is_support &&
    groupId === 'laundry_staff';
  // Отель с включённой Шахматкой после входа попадает сразу в неё —
  // это главный рабочий экран ресепшен (решение 03.11.2026). Если
  // сотруднику раздел «Шахматка» скрыт в карточке («Разделы сайта») —
  // как раньше, на дашборд.
  const sectionsOverride = (store.profile as { web_sections_override?: string[] | null } | null)
    ?.web_sections_override;
  const scheduleFirst =
    (store.platforms.activePlatform as any)?.schedule_enabled === true &&
    (!Array.isArray(sectionsOverride) || sectionsOverride.includes('schedule'));
  const isHotelUser =
    !isFitnessStaff && !isLaundryStaff && !store.profile?.is_superadmin &&
    !store.profile?.is_support && store.profile?.dealer == null;
  const landingRoute = isFitnessStaff
    ? '/concierge/fitness'
    : isLaundryStaff
      ? '/laundry'
      : store.profile?.is_superadmin
      ? '/system/platforms'
      : store.profile?.is_support
        ? '/system/support'
        : store.profile?.dealer != null
          ? '/dealer/platforms'
          : scheduleFirst
            ? '/schedule'
            : '/dashboard';

  return (
    // ErrorBoundary на верхний уровень дерева — раньше любая
    // ошибка рендера давала белый экран без сообщения (#402).
    // Теперь пользователь увидит «Что-то пошло не так» с кнопкой
    // «Перезагрузить» и развёрнутой stack-trace для саппорта.
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
        {/* Публичные */}
        <Route
          path="/login"
          element={
            store.auth.isAuthenticated ? (
              <Navigate to={afterLoginRoute(routerLocation.state, '/')} replace />
            ) : (
              <LoginPage />
            )
          }
        />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route
          path="/signup"
          element={store.auth.isAuthenticated ? <Navigate to="/start" replace /> : <SignupPage />}
        />
        <Route
          path="/start"
          element={
            <PrivateRoute>
              <StartPage />
            </PrivateRoute>
          }
        />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Выбор отеля после входа — полноэкранный, без MainLayout. */}
        <Route
          path="/choose-hotel"
          element={
            <PrivateRoute>
              <HotelPickerPage />
            </PrivateRoute>
          }
        />

        {/* Welcome — приватный, но без MainLayout (полноэкранный) */}
        <Route
          path="/welcome"
          element={
            <PrivateRoute>
              <OnboardingWelcomePage />
            </PrivateRoute>
          }
        />

        {/* Приватные — внутри MainLayout, под гейтом онбординга
            и гейтом fitness_staff (запрещает все не-fitness URL'ы
            для сотрудников фитнес-стенда). */}
        <Route
          element={
            <PrivateRoute>
              <OnboardingGate>
                <FitnessStaffGate>
                  <MainLayout />
                </FitnessStaffGate>
              </OnboardingGate>
            </PrivateRoute>
          }
        >
          <Route index element={<IndexRedirect to={landingRoute} waitPlatform={isHotelUser} />} />
          <Route path="/dashboard" element={<DashboardPage />} />

          {/* Хаускипинг */}
          <Route
            path="/housekeeping/housekeepers/:id"
            element={<HousekeeperProfilePage />}
          />
          <Route
            path="/housekeeping/dashboard"
            element={<HousekeepingDashboardPage />}
          />
          {/* Top-level «Задачи»: сквозной раздел (заявки в Treema-структуре
              + вкладка «Уборки»). Старые пути оставлены как алиасы. */}
          <Route path="/tasks" element={<TasksHubPage />} />
          <Route path="/housekeeping/tasks" element={<TasksPage />} />
          <Route path="/housekeeping/rooms" element={<RoomsPilotRoute />} />
          <Route path="/ops/today" element={<OpsTodayRoute />} />
          {/* Алиас в Настройках: тот же компонент. Точка входа для
              отелей без PMS, где «Номерной фонд» — задача конфигурации,
              а не работы горничной. */}
          <Route path="/settings/rooms" element={<RoomsPage />} />
          <Route path="/housekeeping/checklists" element={<Navigate to="/housekeeping/rooms?tab=categories" replace />} />
          <Route path="/housekeeping/planner" element={<PlannerPage />} />
          <Route path="/housekeeping/inventory" element={<InventoryPage />} />
          <Route path="/housekeeping/reports" element={<HousekeepingReportsPage />} />

          {/* WiFi Кабинет */}
          <Route path="/wifi" element={<Navigate to="/wifi/statistics" replace />} />
          <Route path="/wifi/registration" element={<WifiRegistrationPage />} />
          <Route path="/wifi/active" element={<WifiActivePage />} />
          <Route path="/wifi/trusted" element={<WifiTrustedDevicesPage />} />
          <Route path="/wifi/history" element={<WifiHistoryPage />} />
          <Route path="/wifi/constructor" element={<LegacyConstructorGate><WifiConstructorPage /></LegacyConstructorGate>} />
          <Route path="/wifi/landing-v2" element={<WifiLandingV2ListPage />} />
          <Route path="/wifi/landing-v2/:id" element={<WifiLandingV2EditorPage />} />
          <Route path="/wifi/statistics" element={<WifiStatisticsPage />} />

          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/reviews/group" element={<GroupReputationPage />} />
          <Route path="/surveys" element={<SurveysPage />} />

          {/* Прачечная */}
          <Route path="/laundry" element={<LaundryPage />} />

          {/* Внутренние заявки между отделами */}
          <Route path="/requests" element={<RequestsPage />} />

          {/* CMS: редактор публичного сайта платформы */}
          <Route path="/site" element={<SitePage />} />

          {/* Цифровой двойник: живая карта отеля */}
          <Route path="/twin" element={<HotelMapPage />} />

          {/* Консьерж */}
          <Route path="/concierge/overview" element={<ConciergeOverviewPage />} />
          <Route path="/concierge/app" element={<ConciergeAppPage />} />
          <Route path="/concierge/orders" element={<ConciergeOrdersPage />} />
          {/* Заказ гостя от сотрудника (ресепшен) — под флагом reception-order.json. */}
          <Route path="/concierge/orders/new" element={<ReceptionOrderRoute />} />
          <Route path="/concierge/payments" element={<ConciergePaymentsPage />} />
          <Route path="/concierge/schedule" element={<ConciergeSchedulePage />} />
          <Route path="/concierge/guests" element={<ConciergeGuestsPage />} />
          {/* Аналитика — PMS KPI-дашборд отдельным модулем (гейт
              platform.analytics_enabled — в Sidebar). Старый путь
              /concierge/dashboard оставлен для прямых ссылок. */}
          <Route path="/analytics" element={<ConciergeDashboardPage />} />
          <Route path="/concierge/dashboard" element={<ConciergeDashboardPage />} />
          <Route path="/concierge/loyalty" element={<LoyaltyRoute />} />
          <Route path="/concierge/transfers" element={<ConciergeTransfersPage />} />
          <Route path="/concierge/fitness" element={<ConciergeFitnessPage />} />
          <Route path="/concierge/events" element={<ConciergeEventsPage />} />
          <Route path="/concierge/gift-cards" element={<ConciergeGiftCardsPage />} />

          {/* Conference / MICE (конференц-залы) — Этапы 1–2 */}
          <Route path="/conference/calendar" element={<ConferenceCalendarPage />} />
          <Route path="/conference/events" element={<ConferenceEventsPage />} />
          <Route path="/conference/inquiries" element={<ConferenceInquiriesPage />} />
          {/* Залы + Форматы рассадки + Оборудование — один раздел с вкладками */}
          <Route path="/conference/rooms" element={<ConferenceRoomsHubPage />} />
          <Route path="/conference/seating-layouts" element={<Navigate to="/conference/rooms?tab=layouts" replace />} />
          <Route path="/conference/equipment" element={<Navigate to="/conference/rooms?tab=equipment" replace />} />
          {/* Backward compat redirects.
              Карта и Транспорт переехали в /concierge/app как табы —
              отдельные пункты меню убраны (это контент-настройка, не
              ежедневная работа). Старые ссылки ведут на нужный таб. */}
          <Route path="/concierge/transport" element={<Navigate to="/concierge/app?tab=transport" replace />} />
          <Route path="/concierge/map" element={<Navigate to="/concierge/app?tab=map" replace />} />
          <Route path="/concierge/visa-vouchers" element={<Navigate to="/concierge/orders" replace />} />
          <Route path="/concierge/analytics" element={<Navigate to="/concierge/app" replace />} />
          <Route path="/concierge/catalog" element={<Navigate to="/concierge/app" replace />} />
          <Route path="/concierge/upsell" element={<Navigate to="/concierge/app" replace />} />

          {/* Регистрации (онлайн чек-ин) */}
          <Route path="/registrations/history" element={<CheckinHistoryPage />} />
          <Route path="/registrations/welcome" element={<WelcomePage />} />
          <Route path="/registrations/bookings" element={<BookingsPage />} />
          <Route path="/registrations/documents" element={<DocumentsPage />} />
          <Route path="/registrations/ekeys" element={<EKeysPage />} />

          {/* Шахматка как отдельный модуль (#504). Виден только при
              platform.schedule_enabled — гейт в Sidebar. */}
          <Route path="/schedule" element={<SchedulePage />} />

          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/crm" element={<CrmPage />} />

          {/* Настройки */}
          <Route path="/settings" element={<SettingsRoute />} />
          <Route path="/settings/hub/:tile" element={<SettingsRoute />} />
          <Route path="/settings/about" element={<AboutRoute />} />
          <Route path="/settings/cleanings" element={<CleaningsRoute />} />
          <Route path="/settings/contact" element={<ContactRoute />} />
          <Route path="/settings/payments" element={<PaymentsRoute />} />
          <Route path="/settings/integrations" element={<IntegrationsRoute />} />
          <Route path="/settings/guest-app" element={<ServiceTileRoute tile="guestapp" />} />
          <Route path="/settings/orders" element={<ServiceTileRoute tile="orders" />} />
          <Route path="/settings/checkin" element={<ServiceTileRoute tile="checkin" />} />
          <Route path="/settings/wifi" element={<ServiceTileRoute tile="wifi" />} />
          <Route path="/settings/reviews" element={<ServiceTileRoute tile="reviews" />} />
          <Route path="/settings/tv" element={<ServiceTileRoute tile="tv" />} />
          <Route path="/settings/staff" element={<StaffRoute />} />
          <Route path="/settings/organization" element={<OrganizationPage />} />
          <Route path="/settings/platforms" element={<PlatformsPage />} />
          <Route path="/settings/reports" element={<DocsR3Route />} />
          <Route path="/settings/tariffs" element={<TariffsR3Route />} />
          <Route path="/settings/channel-manager" element={<ChannelManagerPage />} />
          <Route path="/rates-quotas" element={<RatesQuotasPage />} />
          <Route path="/settings/platforms/:platformId/users" element={<PlatformUsersPage />} />
          <Route path="/settings/platforms/:platformId/services/:service" element={<PlatformServicesPage />} />
          <Route path="/settings/mail-templates" element={<MailTemplatesPage />} />
          <Route path="/settings/tv-devices" element={<TvDevicesPage />} />

          {/* Concierge-TV: страница подтверждения привязки (открывается по QR с ТВ) */}
          <Route path="/pair/:code" element={<PairConfirmPage />} />

          {/* Поддержка (hotel-side) */}
          <Route path="/support" element={<SupportPage />} />
          {/* Что нового — лента изменений платформы */}
          <Route path="/changelog" element={<ChangelogPage />} />

          {/* Система (суперадмин) */}
          <Route path="/system/health" element={<SystemHealthPage />} />
          <Route path="/system/flight-watch" element={<FlightWatchPage />} />
          <Route path="/system/platforms" element={<SystemPlatformsPage />} />
          <Route path="/system/dealers" element={<SystemDealersPage />} />
          <Route path="/system/billing" element={<SystemBillingPage />} />
          {/* Кабинет дилера */}
          <Route path="/dealer/platforms" element={<DealerPlatformsPage />} />
          <Route path="/dealer/invoices" element={<DealerInvoicesPage />} />
          <Route path="/dealer/info" element={<DealerInfoPage />} />
          {/* Тикеты отелей дилера — та же страница, что у суперадмина:
              бэкенд сам ограничивает выборку объектами дилера. */}
          <Route path="/dealer/support" element={<SystemSupportPage />} />
          <Route path="/system/leads" element={<SystemLeadsPage />} />
          <Route path="/system/onlinevisa/agents" element={<OnlineVisaAgentsPage />} />
          <Route path="/system/onlinevisa/partner-applications" element={<OnlineVisaPartnerApplicationsPage />} />
          <Route path="/system/news" element={<SystemNewsPage />} />
          <Route path="/system/users" element={<AllUsersPage />} />
          <Route path="/system/support" element={<SystemSupportPage />} />
          <Route path="/system/pending" element={<SystemPendingPage />} />
          <Route path="/system/wifi" element={<WifiMonitoringPage />} />
          <Route path="/system/wifi-setup" element={<WifiSetupPage />} />
          <Route path="/system/partners" element={<SystemPartnersPage />} />

          {/* Маркетинг (суперадмин) */}
          <Route path="/system/marketing/hotels" element={<HotelsTablePage />} />
          <Route path="/system/marketing/kanban" element={<HotelKanbanPage />} />
          <Route path="/system/marketing/analytics" element={<AnalyticsPage />} />
          <Route path="/system/marketing/campaigns" element={<CampaignsPage />} />
          <Route path="/system/marketing/send" element={<UnisenderSendPage />} />
          <Route path="/system/marketing/lists" element={<UnisenderListsPage />} />
          <Route path="/system/marketing/templates" element={<UnisenderTemplatesPage />} />
          <Route path="/system/marketing/campaign-stats" element={<UnisenderStatsPage />} />
          <Route path="/system/marketing/tracking" element={<CampaignTrackingPage />} />

          {/* 404 */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>

        {/* Любой незалогиненный → login */}
        <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
});
